'use client';
// status: updated
import { useState, useRef, useEffect } from "react";
import { Images, Videos } from "@assets/assets";
import Image from "next/image";
import DeferredVideo from "@/components/DeferredVideo";
import { ensureGsap } from "@/lib/EnsureGsap";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { whenIdle } from "@/lib/whenIdle";

interface WhychooseConstantProps {
  title: React.ReactNode;
  subtitle: string;
  description: string;
  button: React.ReactNode;
}

export default function WhychooseConstant({
  title,
  subtitle,
  description,
  button,
}: WhychooseConstantProps) {
  const sectionRef = useRef<HTMLDivElement | null>(null);
  const marqueeWrapperRef = useRef<HTMLDivElement>(null);
  const marqueeContentRef = useRef<HTMLDivElement>(null);
  const tlRef = useRef<any | null>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const wrapper = marqueeWrapperRef.current;
    const content = marqueeContentRef.current;
    if (!wrapper || !content) return;

    let cancelled = false;

    const initMarquee = async () => {
      const { gsap } = await ensureGsap();
      if (cancelled) return;

      // Use requestAnimationFrame to ensure we measure after the first paint
      requestAnimationFrame(() => {
        if (!content) return;

        const contentWidth = content.scrollWidth / 2;
        gsap.set(content, { x: 0 });

        tlRef.current = gsap.to(content, {
          x: -contentWidth,
          duration: 45,
          ease: "none",
          repeat: -1,
          // Using modifiers can be expensive; try to use a simple loop if possible
          modifiers: {
            x: gsap.utils.unitize((x) => parseFloat(x) % contentWidth),
          },
        });
      });
    };

    // Idle-deferred: the marquee is far below the fold; measuring scrollWidth
    // during hydration causes a forced reflow and adds to mobile TBT.
    const cancelIdle = whenIdle(initMarquee);

    return () => {
      cancelled = true;
      cancelIdle();
      tlRef.current?.kill();
    };
  }, []);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;

    let ctx: any;
    let cancelled = false;

    const cancelIdle = whenIdle(async () => {
      if (cancelled) return;
      const { gsap } = await ensureGsap();
      if (cancelled) return;

      ctx = gsap.context(() => {
        const tl = gsap.timeline({
          scrollTrigger: {
            trigger: el,
            // Start when the top of the section hits 80% of the viewport height
            start: "top 90%",
            end: "bottom 20%",
            scrub: true,
            // Update the state when it comes into view
            onEnter: () => setInView(true),
            // Optional: set back to false when leaving if you want it to reset
            // onLeaveBack: () => setInView(false),
          },
        });

        tl.to(el.querySelector(".why-choose-main"), {
          y: 0,
          opacity: 1,
          ease: "power1.out",
          duration: 1,
        });
      }, el);
    });

    return () => {
      cancelled = true;
      cancelIdle();
      ctx?.revert?.();
    };
  }, []);

  const logos = [
    { src: Images?.logoSet.bonusway, name: "Bonusway" },
    { src: Images?.logoSet.delMonte, name: "DelMonte" },
    { src: Images?.logoSet.festivity, name: "Festivity" },
    { src: Images?.logoSet.nasscom, name: "NASSCOM" },
    { src: Images?.logoSet.paceFunding, name: "PaceFunding" },
    { src: Images?.logoSet.rawbank, name: "Rawbank" },
    { src: Images?.logoSet.rayTech, name: "RayTech" },
    { src: Images?.logoSet.unitedNations, name: "United Nations" },
    { src: Images?.logoSet.volkswagen, name: "Volkswagen" },
  ];

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const isMobile = useIsMobile()

  return (
    <section
      ref={sectionRef}
      className="why-choose-constant bg-[#000000] w-full relative overflow-hidden"
    >
      <div className="texture-bg mix-blend-overlay  w-full h-full absolute top-0 left-0 z-0">
        {" "}
        {!isMobile && <div className="absolute inset-0 z-0">
          <Image
            src={Images?.texture}
            alt="texture"
            fill
            className="object-cover mix-blend-overlay opacity-60"
            sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
          />
        </div>}
      </div>
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px] absolute right-0 top-0 transform translate-x-1/3 -translate-y-1/3" />
      <div className="w-[907.05px] h-[907.05px] bg-[#225bd4]/40 rounded-full blur-[333.68px]  absolute left-0 bottom-0  -translate-x-1/3 translate-y-1/3" />

      <div className="max-w-[5000px] why-choose-main  mx-auto w-[calc(100%_-_60px)] pt-[20px] lg:pt-[80px] relative z-1">
        <div className="why-choose-main-content flex gap-0 ">
          <div className="px-0 lg:px-6 pb-0 lg:pb-10 pt-6 w-full">
            <p className="w-full justify-center text-white text-base font-medium uppercase tracking-tight lg:mb-[30px]">
              {subtitle}
            </p>
            <div className="flex flex-col gap-[20px] pb-4 w-full">
              {/* TOP ROW: Title, Video, and Button */}
              <div className="flex flex-col 2xl:flex-row items-start justify-between gap-8 w-full">
                {/* 1. Video Element (Now part of the flow) */}
                {inView &&
                  typeof window !== "undefined" &&
                  window.innerWidth > 1560 && (
                    <div className="flex-1 flex justify-center items-center min-w-[400px]">
                      <DeferredVideo
                        src={Videos.blob}
                        className="w-[350px] h-auto object-cover rounded-xl"
                        preload="none"
                        ariaLabel="blob video"
                      />
                    </div>
                  )}

                {/* 2. Title */}
                <div>
                  <h2 className="max-w-[18ch] text-white text-[length:var(--section-title)] font-medium clash-font leading-[110%]">
                    {title}
                  </h2>
                  <p className="max-w-[80ch] text-white/50 pt-[20px] lg:pt-[0px] text-[length:var(--hero-para)] font-normal leading-[160%]">
                    {description}
                  </p>
                </div>

                {/* 3. Button */}
                <div className="2xl:self-start">{button}</div>
              </div>

              {/* BOTTOM ROW: Description */}
            </div>
          </div>
        </div>
        <div className="w-full lg:flex items-center justify-center box-border">
          <div className="relative w-full aspect-video max-h-[730px] overflow-hidden group cursor-pointer border border-white/10 mt-10">
            {!isVideoPlaying ? (
              <div
                className="absolute inset-0 z-10"
                onClick={() => setIsVideoPlaying(true)}
              >
                <Image
                  src={Images?.YoutubeThumbNew}
                  alt="Video Thumbnail"
                  fill
                  sizes="(max-width: 1024px) 100vw, 50vw"
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 flex items-center justify-center bg-black/10 group-hover:bg-black/30 transition-all">
                  <div className="relative w-[68px] h-[48px] flex items-center justify-center rounded-[12px] bg-[#FF0000] transition-transform duration-300 group-hover:scale-110 shadow-xl">
                    <div
                      className="w-0 h-0 
                       border-y-[10px] border-y-transparent 
                       border-l-[18px] border-l-white 
                       ml-1"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <iframe
                className="w-full h-full"
                src="https://www.youtube.com/embed/e5XW9PPsAPY?autoplay=1&rel=0"
                title="YouTube video player"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; web-share"
                allowFullScreen
              ></iframe>
            )}
          </div>
        </div>
        {/* <iframe className='w-full aspect-[16/7]'  src="https://www.youtube.com/embed/e5XW9PPsAPY?si=kUj77nx8dOmFgE_j" title="YouTube video player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" referrerpolicy="strict-origin-when-cross-origin" allowfullscreen></iframe> */}
        <p className="text-center text-white text-[length:var(--hero-para)] py-2 lg:py-10 font-normal uppercase tracking-wide pt-10 lg:pt-15">
          Trusted By
        </p>
      </div>

      <div
        ref={marqueeWrapperRef}
        className="overflow-hidden w-full client-logos py-[20px] xl:pb-[70px]"
      >
        <div
          ref={marqueeContentRef}
          className="flex gap-[50px] whitespace-nowrap will-change-transform max-w-[max-content]"
        >
          {[...logos, ...logos]?.map((logo, i) => (
            <div key={i} className="group relative flex-shrink-0 w-[10vw] ...">
              <Image
                src={logo.src}
                alt={logo.name}
                width={200}
                height={140}
                className="max-h-[140px] mx-auto object-contain"
                sizes="200px" // More accurate size helps the browser
                loading="lazy" // DO NOT use priority here
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
